import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';

interface InputReviewModalProps {
  phaseInputs: any;
  onClose: () => void;
  onRunPhase: () => void;
  getPersonaName?: (personaId: string) => string;
}

export const InputReviewModal: React.FC<InputReviewModalProps> = ({
  phaseInputs,
  onClose,
  onRunPhase,
  getPersonaName
}) => {
  const { darkMode } = useTheme();
  
  if (!phaseInputs) return null;
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`rounded-lg shadow-xl w-full max-w-2xl ${darkMode ? 'bg-gray-800' : 'bg-white'} p-6`}>
        <h3 className="text-lg font-medium mb-4">Review Phase Inputs</h3>
        
        {/* Prompt inputs */}
        {phaseInputs.prompts && phaseInputs.prompts.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Prompts</h4>
            {phaseInputs.prompts.map((prompt, index) => (
              <div key={index} className={`p-3 rounded mb-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                <div className="flex justify-between mb-1">
                  <span className="text-sm font-medium">Mode: {prompt.mode || 'simple'}</span>
                  {prompt.mode !== 'simple' && prompt.original_text && (
                    <span className="text-xs text-gray-500">
                      Transformed
                    </span>
                  )}
                </div>
                <div className={`p-2 rounded ${darkMode ? 'bg-gray-800' : 'bg-white'} whitespace-pre-wrap text-sm`}>
                  {prompt.text}
                </div>
              </div>
            ))}
          </div>
        )}
        
        {/* Artifact inputs */}
        {phaseInputs.artifacts && phaseInputs.artifacts.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Artifacts</h4>
            <div className="max-h-40 overflow-y-auto">
              {phaseInputs.artifacts.map((artifact, index) => (
                <div key={index} className={`p-2 rounded mb-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-sm font-medium">{artifact.name}</div>
                  <div className="text-xs text-gray-500">Type: {artifact.type}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Persona inputs */}
        {phaseInputs.personas && phaseInputs.personas.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Personas</h4>
            <div className="max-h-40 overflow-y-auto">
              {phaseInputs.personas.map((persona, index) => (
                <div key={index} className={`p-2 rounded mb-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-sm font-medium">
                    {getPersonaName 
                      ? getPersonaName(persona.persona_id)
                      : (persona.persona_name || persona.persona_id)}
                  </div>
                  <div className="text-xs text-gray-500">Role: {persona.role}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Tool inputs */}
        {phaseInputs.tools && phaseInputs.tools.length > 0 && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Tools</h4>
            <div className="max-h-40 overflow-y-auto">
              {phaseInputs.tools.map((tool, index) => (
                <div key={index} className={`p-2 rounded mb-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                  <div className="text-sm font-medium">{tool}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        
        <div className="flex justify-end space-x-3 mt-4">
          <button
            className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-200 hover:bg-gray-300'}`}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            onClick={onRunPhase}
          >
            Run Phase
          </button>
        </div>
      </div>
    </div>
  );
};
